import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, CreditCard, Calendar, Clock, AlertTriangle, Sparkles } from "lucide-react";

const SubscriptionPage = () => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [paymentPolling, setPaymentPolling] = useState(null);

  const fetchAll = async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/subscription/status`, { headers: h }),
        axios.get(`${API}/subscription/plans`),
      ]);
      setStatus(s.data);
      setPlans(p.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && !paymentPolling) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams]); // eslint-disable-line

  const pollPaymentStatus = async (sessionId, attempts) => {
    setPaymentPolling(`Preverjam plačilo... (${attempts + 1}/8)`);
    if (attempts >= 8) {
      toast.error("Preverjanje plačila trajajo predolgo. Osveži stran čez minuto.");
      setPaymentPolling(null);
      return;
    }
    try {
      const h = { Authorization: `Bearer ${token}` };
      const r = await axios.get(`${API}/subscription/status/${sessionId}`, { headers: h });
      if (r.data.payment_status === "paid") {
        toast.success("Plačilo uspešno! Naročnina je aktivirana.");
        setPaymentPolling(null);
        // Clear session_id from URL
        window.history.replaceState({}, "", "/narocnina");
        fetchAll();
        return;
      }
      if (r.data.status === "expired") {
        toast.error("Seja plačila je potekla.");
        setPaymentPolling(null);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch (e) {
      toast.error("Napaka pri preverjanju plačila.");
      setPaymentPolling(null);
    }
  };

  const handleCheckout = async (planKey) => {
    setProcessing(planKey);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const r = await axios.post(`${API}/subscription/checkout`, {
        plan: planKey,
        origin_url: window.location.origin,
      }, { headers: h });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri plačilu");
      setProcessing(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const isActive = status?.status === "active" || status?.status === "trial";
  const isPredsednik = user?.vloga === "predsednik" || user?.vloga === "administrator";

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
          <CreditCard className="h-8 w-8" />Naročnina
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Upravljanje naročnine za "Dober pogled app"</p>
      </div>

      {paymentPolling && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="spinner w-5 h-5 border-2 border-blue-600/30 border-t-blue-600" />
            <p className="text-sm">{paymentPolling}</p>
          </CardContent>
        </Card>
      )}

      {/* Current status card */}
      <Card className={isActive ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}>
        <CardHeader>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-lg">{status?.ld_naziv}</CardTitle>
              <CardDescription>Trenutno stanje naročnine</CardDescription>
            </div>
            {status?.status === "trial" && <Badge className="bg-amber-500 text-white"><Clock className="h-3 w-3 mr-1" />Preizkusno</Badge>}
            {status?.status === "active" && <Badge className="bg-green-600 text-white"><CheckCircle2 className="h-3 w-3 mr-1" />Aktivna</Badge>}
            {status?.status === "expired" && <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Potekla</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {status?.status === "trial" && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-amber-600" />
              <span>Brezplačno preizkusno obdobje se izteče v <strong>{status.days_left} dneh</strong> ({new Date(status.trial_ends_at).toLocaleDateString("sl-SI")})</span>
            </div>
          )}
          {status?.status === "active" && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-green-600" />
              <span>Naročnina aktivna do <strong>{new Date(status.subscription_ends_at).toLocaleDateString("sl-SI")}</strong> ({status.days_left} dni)</span>
            </div>
          )}
          {status?.status === "expired" && (
            <div className="text-sm text-red-700">
              Naročnina je potekla. Za nadaljnjo uporabo obnovite plačilo.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      {isPredsednik ? (
        <div>
          <h2 className="text-xl font-semibold mb-3">Izberi paket</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map(p => (
              <Card key={p.key} className={p.key === "yearly" ? "border-primary ring-2 ring-primary/10" : ""} data-testid={`plan-${p.key}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{p.label}</CardTitle>
                    {p.key === "yearly" && <Badge className="bg-primary"><Sparkles className="h-3 w-3 mr-1" />Prihranek 48€</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    {p.key === "yearly" ? (
                      <>
                        <div className="text-4xl font-bold text-primary">{(p.amount / 12).toFixed(2).replace(".00","")}€<span className="text-lg text-muted-foreground font-normal"> /mesec</span></div>
                        <div className="text-sm text-muted-foreground mt-1">
                          <span className="font-semibold text-foreground/70">{p.amount.toFixed(2).replace(".00","")} € letno</span> · zaračunano 1× letno
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-4xl font-bold text-primary">{p.amount.toFixed(2).replace(".00","")}€<span className="text-lg text-muted-foreground font-normal"> /mesec</span></div>
                        <div className="text-sm text-muted-foreground mt-1">zaračunano mesečno</div>
                      </>
                    )}
                  </div>
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Vse funkcije platforme</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Neomejeno članov</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />PWA / mobilna namestitev</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Izvoz v Excel/PDF</li>
                    {p.key === "yearly" && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /><strong>25% ceneje kot mesečno</strong></li>}
                  </ul>
                  <Button
                    className="w-full"
                    onClick={() => handleCheckout(p.key)}
                    disabled={!!processing}
                    variant={p.key === "yearly" ? "default" : "outline"}
                    data-testid={`buy-${p.key}`}
                  >
                    {processing === p.key ? "Preusmerjam..." : `Plačaj ${p.amount.toFixed(2)}€`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Plačilo poteka preko Stripe. Vaša naročnina se ne obnavlja avtomatsko – pred iztekom vas obvestimo.
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Naročnino lahko upravlja samo predsednik lovske družine.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionPage;
